import React from 'react';
import { View, Pressable } from 'react-native';
import { useTheme } from '../theme';
import Txt from './Txt';
import Icon from './Icon';
import { Input, Field } from './Input';
import { Row } from './Layout';
import { searchGuests, type Guest } from '../services/crm/guest';

/**
 * GuestPicker — a CRM-like guest selector used by Reservations & Support.
 *
 * Type a name/phone → live-searches sk-CRM contacts (via the adapter) → shows a
 * dropdown of matches. Pick one (sets contactId), or just type a new name+phone
 * and the parent's find-or-create dedups by phone at submit. Purely controlled:
 * the parent owns {contactId, name, phone}; this only surfaces search + selection.
 */

interface GuestPickerProps {
  name: string;
  phone: string;
  contactId?: string;
  onChange: (v: { name: string; phone: string; contactId?: string }) => void;
  namePlaceholder?: string;
}

export function GuestPicker({ name, phone, contactId, onChange, namePlaceholder = 'Guest name' }: GuestPickerProps) {
  const t = useTheme();
  const c = t.colors;
  const [results, setResults] = React.useState<Guest[]>([]);
  const [open, setOpen] = React.useState(false);
  const [loading, setLoading] = React.useState(false);

  // Debounced CRM search on the name field. A picked contact suppresses search
  // (we don't want the dropdown reopening over a confirmed selection).
  React.useEffect(() => {
    if (contactId) { setResults([]); setOpen(false); return; }
    const q = name.trim();
    if (q.length < 2) { setResults([]); setOpen(false); return; }
    let cancelled = false;
    setLoading(true);
    const h = setTimeout(() => {
      searchGuests(q)
        .then((rows) => { if (!cancelled) { setResults(rows); setOpen(rows.length > 0); } })
        .catch(() => { if (!cancelled) { setResults([]); setOpen(false); } })
        .finally(() => { if (!cancelled) setLoading(false); });
    }, 250);
    return () => { cancelled = true; clearTimeout(h); };
  }, [name, contactId]);

  const pick = (g: Guest) => {
    onChange({ name: g.name, phone: g.phone ?? '', contactId: g.id });
    setOpen(false);
    setResults([]);
  };

  // Editing name/phone after a pick clears the contactId → back to free entry.
  const editName = (v: string) => onChange({ name: v, phone, contactId: undefined });
  // Country code is fixed to +91 (see the adornment below) — the field holds only
  // the bare mobile digits (max 10). The adapter prepends +91 before CRM, which
  // requires E.164. Strip anything non-numeric so paste/format quirks can't break it.
  const editPhone = (v: string) => onChange({ name, phone: v.replace(/\D/g, '').slice(0, 10), contactId: undefined });

  return (
    <Row gap={14} style={{ alignItems: 'flex-start' }}>
      <Field label="Guest name" style={{ flex: 1 }}>
        <View style={{ position: 'relative' }}>
          <Input placeholder={namePlaceholder} value={name} onChangeText={editName} />
          {contactId && (
            <View style={{ position: 'absolute', right: 8, top: 8 }}>
              <Icon name="check" size={14} color={c.success} strokeWidth={2} />
            </View>
          )}
          {open && (
            <View
              style={{
                position: 'absolute', top: 38, left: 0, right: 0, zIndex: 20,
                backgroundColor: c.card, borderWidth: 1, borderColor: c.line,
                borderRadius: 8, overflow: 'hidden',
                shadowColor: '#000', shadowOpacity: 0.08, shadowRadius: 8, shadowOffset: { width: 0, height: 4 },
              }}
            >
              {results.map((g) => (
                <Pressable
                  key={g.id}
                  onPress={() => pick(g)}
                  style={{ paddingVertical: 8, paddingHorizontal: 10, borderBottomWidth: 1, borderBottomColor: c.line }}
                >
                  <Txt size="sm" weight="medium">{g.name}</Txt>
                  {!!g.phone && <Txt size="xs" color="ink3">{g.phone}</Txt>}
                </Pressable>
              ))}
              <View style={{ paddingVertical: 6, paddingHorizontal: 10, backgroundColor: c.paper2 }}>
                <Txt size="xs" color="ink4">Pick a guest, or keep typing to create a new one</Txt>
              </View>
            </View>
          )}
        </View>
      </Field>
      <Field label="Phone" style={{ flex: 1 }}>
        <Row gap={0} style={{ alignItems: 'stretch' }}>
          <View style={{
            justifyContent: 'center', paddingHorizontal: 10,
            borderWidth: 1, borderRightWidth: 0, borderColor: c.line,
            borderTopLeftRadius: t.radii[2], borderBottomLeftRadius: t.radii[2],
            backgroundColor: c.paper2,
          }}>
            <Txt size="md" color="ink2" weight="medium">+91</Txt>
          </View>
          <View style={{ flex: 1 }}>
            <Input
              placeholder="98765 43210"
              value={phone}
              onChangeText={editPhone}
              keyboardType="phone-pad"
              style={{ borderTopLeftRadius: 0, borderBottomLeftRadius: 0 }}
            />
          </View>
        </Row>
      </Field>
    </Row>
  );
}
